import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "./partials/Sidebar";
import Nav from "./partials/Nav";
import Header from "./partials/Header";
import PortalFooter from "./partials/PortalFooter";
import GetApp from "./partials/GetApp";
import PageLoader from "../modules/shared/components/PageLoader";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { me, logout } from "../api/AuthApi";
import {
  updateCurrentUser,
  updateFunnel,
  updateOnboardingSteps,
  updatePromoteSettings,
  updatePermissions,
} from "../redux/actionCreators";
import { useSignOut } from "react-auth-kit";
import { toastrOnTopCenter } from "../utils/toastr";
import Cookies from "js-cookie";
import NetworkError from "../pages/NetworkError";
import { PermissionsContext } from "../context/permissionsContext";
import { useLocation } from "react-router-dom";
import { getDocumentTitle } from "../utils/generalHelper";
import { LEVEL_ADMIN } from "../utils/userPermissions";

/*
import {SocketContext} from "../context/socketContext";
import {useSocket} from "../services/pusherSocket/socketHook";
import {CHAT_MESSAGE_SENT} from "../services/pusherSocket/socketEventTypes";
*/

function authenticatedLayout(WrappedComponent) {
  return function AuthenticatedLayout(props) {
    const signOut = useSignOut();
    const location = useLocation();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [cssLoaded, setCssLoaded] = useState(false);
    const currentLayout = Cookies.get("currentLayout");
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const linkExists = document.querySelector(
      "link[href='/assets/css/authenticated-layout.css']"
    );
    const userPermissions = useSelector(
      (state) => state.permissions.userPermissions
    );
    const isW9Form = location.pathname.includes(
      "/affiliate-program/initiate-withdraw/w9-form"
    );
    /*
    const [latestMessage, setLatestMessage] = useState({});

   useSocket({
      type: CHAT_MESSAGE_SENT,
      callBack: (payload) => receivedChatMessage(payload),
    }); */

    useEffect(() => {
      document.title = `R2F - ${getDocumentTitle(location)}`;
      if (currentLayout !== "authenticated" || !linkExists) {
        const links = document.querySelectorAll("link");
        for (let link of links) {
          const href = link.getAttribute("href");
          if (href === "/assets/css/guest-layout.css") {
            link.remove();
          } else if (href === "/assets/css/auth-layout.css") {
            link.remove();
          }
        }

        if (!linkExists) {
          const link = document.createElement("link");
          link.setAttribute("rel", "stylesheet");
          link.setAttribute("type", "text/css");
          link.setAttribute("href", "/assets/css/authenticated-layout.css");
          document.head.appendChild(link);
          link.onload = () => {
            Cookies.set("currentLayout", "authenticated", {
              expires: 7,
              path: "",
            });
            setCssLoaded(true);
          };
        }
      }
    }, []);

    useEffect(() => {
      refreshCurrenUserInfo();

      document.body.classList.remove("bg-default");
      window.scrollTo(0, 0);

      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);

      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);

      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      };
    }, []);

    const refreshCurrenUserInfo = async () => {
      try {
        const storedToken = localStorage.getItem("fcmToken");
        const response = await me({
          device_token: storedToken,
          device_type: "web",
        });

        const authUser = response.data.user;

        let funnel = {
          exclusive_member: authUser.exclusive_member,
          guest_user_funnel: authUser.guest_user_funnel,
          master_class_funnel: authUser.master_class_funnel,
          blueprint_funnel: authUser.blueprint_funnel,
          dollar_trial_funnel: authUser.dollar_trial_funnel,
          annual_special_funnel: authUser.annual_special_funnel,
          ninety_day_intro_plan: authUser.ninety_day_intro_plan,
          five_month_intro_plan: authUser.five_month_intro_plan,
          card_free_trial_funnel: authUser.card_free_trial_funnel,
          free_registration_no_card: authUser.free_registration_no_card,
          instant_signup_payment: authUser.instant_signup_payment,
          live_opportunity_call_funnel: authUser.live_opportunity_call_funnel,
        };
        const userRole = authUser.permissions.includes(LEVEL_ADMIN)
          ? "admin"
          : "user";
        Cookies.set("hasUser", userRole, {
          expires: 7,
          path: "/",
        });

        dispatch(updateCurrentUser(authUser));
        dispatch(updateOnboardingSteps(authUser.onboarding_steps_state));
        dispatch(updatePromoteSettings(authUser.promote_settings));
        dispatch(updateFunnel(funnel));
        dispatch(updatePermissions(authUser.permissions));

        setIsLoading(false);
      } catch (error) {
        throw new Error("Something went wrong at user data fetching.");
      }
    };

    const handleLogout = () => {
      logout()
        .then(() => {
          signOut();
          sessionStorage.removeItem("isGetAppClosed");
          sessionStorage.removeItem("selectedRange");
          sessionStorage.removeItem("affiliateSubmenu");
        })
        .catch((error) => {
          toastrOnTopCenter("Something went wrong, try later", "error");
        });
    };

    const hasPermission = (permission) => {
      return userPermissions.includes(permission);
    };

    /* const receivedChatMessage = (payload) => {
      setLatestMessage(payload.message);
    };
    */

    if (currentLayout !== "authenticated" || !linkExists) {
      return <PageLoader />;
    }
    return (
      <>
        {/* <SocketContext.Provider value={{latestMessage}}> */}
        <HelmetProvider>
          <Helmet>
            {/* <script src="https://cdn.usebootstrap.com/bootstrap/4.4.1/js/bootstrap.min.js?v1.0"></script> */}
            <script src="https://stackpath.bootstrapcdn.com/bootstrap/4.4.1/js/bootstrap.min.js"></script>
            <script src="/assets/js/argon.js?v1.0"></script>
          </Helmet>
        </HelmetProvider>

        <PermissionsContext.Provider value={{ hasPermission }}>
          <Sidebar handleLogout={handleLogout} />
          <div
            className={"main-content main-authenticated-container"}
            id="panel"
          >
            <Nav handleLogout={handleLogout} />
            <Header />
            {isOnline && (
              <div
                className={`${
                  isW9Form ? "" : "container-fluid"
                } mt--6 main-authenticated-item`}
              >
                {isLoading ? (
                  <>
                    <div
                      style={{ height: "100vh" }}
                      className="d-flex align-items-center"
                    >
                      <PageLoader />
                    </div>
                  </>
                ) : (
                  <WrappedComponent
                    {...props}
                    refreshCurrenUserInfo={refreshCurrenUserInfo}
                  />
                )}
                <PortalFooter />
              </div>
            )}

            <NetworkError isOnline={isOnline} />
          </div>
        </PermissionsContext.Provider>
        <GetApp />
        {/* </SocketContext.Provider> */}
      </>
    );
  };
}

export default authenticatedLayout;
